import React from 'react';
import { cn } from './ui/utils';
import { brandPalette } from './brand-scaffold';

interface IconProps {
  className?: string;
  /** Background fill color. Defaults to brand green. */
  bgColor?: string;
  /** Line/stroke color. Defaults to black. */
  lineColor?: string;
  /** Text color for CALL / ANYTHING. Defaults to lineColor. */
  textColor?: string;
  /** Whether to show the CALL / ANYTHING text labels. Defaults to true. */
  showText?: boolean;
}

/**
 * 品牌核心图腾的 SVG 内部元素（无外层 <svg> 包裹）
 * viewBox 坐标系为 0 0 200 200，可直接嵌入 <svg> 或 <pattern> 中复用。
 */
export function BrandIconShapes({
  lineColor = 'black',
  textColor,
  showText = true,
  showBorder = true,
}: Pick<IconProps, 'lineColor' | 'textColor' | 'showText'> & { showBorder?: boolean }) {
  const tc = textColor || lineColor;
  return (
    <>
      {/* Outer border — placed exactly on tile edge so adjacent tiles share one centerline stroke */}
      {showBorder && (
        <rect x="0" y="0" width="200" height="200" fill="none" stroke={lineColor} strokeWidth="5" strokeLinecap="square" />
      )}

      <g stroke={lineColor} strokeWidth="5" fill="none" strokeLinecap="square">
        {/* Four corner diagonals → converge on inner square corners */}
        <line x1="0" y1="0" x2="60" y2="60" />
        <line x1="200" y1="0" x2="140" y2="60" />
        <line x1="0" y1="200" x2="60" y2="140" />
        <line x1="200" y1="200" x2="140" y2="140" />

        {/* Inner centered square */}
        <rect x="60" y="60" width="80" height="80" />

        {/* Circle inscribed in inner square */}
        <circle cx="100" cy="100" r="40" />

        {/* X cross inside inner square */}
        <line x1="60" y1="60" x2="140" y2="140" />
        <line x1="140" y1="60" x2="60" y2="140" />
      </g>

      {/* Text labels */}
      {showText && (
        <g fill={tc} fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif" fontWeight="900" letterSpacing="0.05em">
          <text x="12" y="38" fontSize="22" textAnchor="start">CALL</text>
          <text x="188" y="180" fontSize="22" textAnchor="end">ANYTHING</text>
        </g>
      )}
    </>
  );
}

export function IconBrand({
  className,
  bgColor = brandPalette.green,
  lineColor = 'black',
  textColor,
  showText = true,
}: IconProps) {
  return (
    <svg
      viewBox="0 0 200 200"
      className={cn('block', className)}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Background fill */}
      <rect width="200" height="200" fill={bgColor} />
      <BrandIconShapes lineColor={lineColor} textColor={textColor} showText={showText} />
    </svg>
  );
}

/**
 * 产品层图标：通用平面 2D 风格，展现各自产品功能/定位
 * 每个图标使用对应品牌色底 + 黑色线条图形
 */

interface ProductIconProps {
  className?: string;
  bgColor?: string;
  color?: string;
}

// Protocol: 插头（与首页 ARCHITECTURE / PROTOCOL 柱 Lucide Plug 一致），路径来自 lucide plug，缩放后与其它 48 产品图标体量接近
export function IconProtocol({ className, bgColor = brandPalette.green, color = 'black' }: ProductIconProps) {
  return (
    <svg viewBox="0 0 48 48" className={cn('block', className)} fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" fill={bgColor} />
      <g
        transform="translate(24 24) scale(1.5) translate(-12 -12)"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        <path d="M12 22v-5" />
        <path d="M9 8V2" />
        <path d="M15 8V2" />
        <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
      </g>
    </svg>
  );
}

// Client: 应用/终端（极简长方形带顶栏和光标块，代表操作界面）
export function IconClient({ className, bgColor = brandPalette.blue, color = 'black' }: ProductIconProps) {
  return (
    <svg viewBox="0 0 48 48" className={cn('block', className)} fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" fill={bgColor} />
      <g stroke={color} strokeWidth="3" strokeLinecap="square" strokeLinejoin="miter" fill="none">
        <rect x="6" y="12" width="36" height="24" />
        <line x1="6" y1="20" x2="42" y2="20" />
        <rect x="12" y="26" width="8" height="4" fill={color} />
      </g>
    </svg>
  );
}

// Caller: 发起方（左侧节点发射向右的长箭头，代表调用动作）
export function IconCaller({ className, bgColor = brandPalette.teal, color = 'black' }: ProductIconProps) {
  return (
    <svg viewBox="0 0 48 48" className={cn('block', className)} fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" fill={bgColor} />
      <g stroke={color} strokeWidth="3" strokeLinecap="square" strokeLinejoin="miter" fill="none">
        <rect x="10" y="16" width="12" height="16" />
        <line x1="22" y1="24" x2="38" y2="24" />
        <polyline points="32,18 38,24 32,30" />
      </g>
    </svg>
  );
}

// Responder: 响应方/目标（向右的箭头直接射入右侧节点，代表被调用）
export function IconResponder({ className, bgColor = brandPalette.orange, color = 'black' }: ProductIconProps) {
  return (
    <svg viewBox="0 0 48 48" className={cn('block', className)} fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" fill={bgColor} />
      <g stroke={color} strokeWidth="3" strokeLinecap="square" strokeLinejoin="miter" fill="none">
        <rect x="29" y="16" width="12" height="16" />
        <line x1="10" y1="24" x2="26" y2="24" />
        <polyline points="20,18 26,24 20,30" />
      </g>
    </svg>
  );
}

// Platform: 平台/基础设施（层叠方块/网格枢纽）
export function IconPlatform({ className, bgColor = brandPalette.purple, color = 'black' }: ProductIconProps) {
  return (
    <svg viewBox="0 0 48 48" className={cn('block', className)} fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="0" fill={bgColor} />
      <g stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none">
        {/* 3 stacked layers (isometric-ish) */}
        <polygon points="24,10 40,18 24,26 8,18" />
        <polyline points="8,24 24,32 40,24" />
        <polyline points="8,30 24,38 40,30" />
      </g>
    </svg>
  );
}

// Selfhost: 私有部署（服务器 + 锁）
export function IconSelfhost({ className, bgColor = brandPalette.yellow, color = 'black' }: ProductIconProps) {
  return (
    <svg viewBox="0 0 48 48" className={cn('block', className)} fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="0" fill={bgColor} />
      <g stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none">
        {/* Server box */}
        <rect x="10" y="8" width="28" height="12" />
        <rect x="10" y="20" width="28" height="12" />
        {/* Server indicator dots */}
        <circle cx="32" cy="14" r="1.5" fill={color} stroke="none" />
        <circle cx="32" cy="26" r="1.5" fill={color} stroke="none" />
        {/* Lock icon below */}
        <rect x="19" y="36" width="10" height="7" />
        <path d="M21 36v-2a3 3 0 016 0v2" />
      </g>
    </svg>
  );
}
